import { ContentBlock, ContentState, EditorState, genKey, Modifier, RichUtils, SelectionState } from 'draft-js';
import Immutable from 'immutable';
import { BLOCK_TYPE } from './rendererHelpers';

/**
 * Function for prepare children data for render in table
 * @param editorState
 * @param children
 * @param offsetKey
 * @returns {null|*[]}
 */
export const getArrayForTable = (editorState, children, offsetKey) => {
  const stateBlocks = editorState
    .getCurrentContent()
    .getBlockMap()
    ?.toJS();

  offsetKey = children.find((itemBlock) => itemBlock.props['data-offset-key'] === offsetKey)?.key;

  if (!offsetKey || !stateBlocks) {
    return null;
  }

  const table = [];
  let row = [];
  children.forEach((item) => {
    row.push(item);
    if (stateBlocks[item.key].data.end) {
      table.push(row);
      row = [];
    }
  });

  return table;
};

/**
 * Function push new blocks for table in editor state
 * @param editorState
 * @param structure
 * @returns {EditorState}
 */
export const commitTable = (editorState, structure = [3, 4]) => {
  const currentContent = editorState.getCurrentContent();
  const selection = editorState.getSelection();
  let blocks = currentContent.getBlockMap().toArray();

  let pushIndex = blocks.findIndex((block) => block.getKey() === selection.getFocusKey());

  const table = [];
  const focus = genKey();

  for (let i = 0; i < structure[0]; i += 1) {
    for (let j = 0; j < structure[1]; j += 1) {
      table.push(
        new ContentBlock({
          key: i === 0 && j === 0 ? focus : genKey(),
          type: BLOCK_TYPE.TABLE_ROW_ITEM,
          text: '',
        }),
      );
    }

    const block = new ContentBlock({
      key: genKey(),
      type: BLOCK_TYPE.TABLE_ROW_ITEM,
      text: '',
      data: Immutable.Map({ end: true }),
    });

    table.push(block);
  }

  blocks.splice(pushIndex + 1, 0, ...table);

  editorState = EditorState.push(editorState, ContentState.createFromBlockArray(blocks), 'insert-fragment');
  return EditorState.forceSelection(editorState, SelectionState.createEmpty(focus));
};

/**
 * Handler for update state if selected table and press enter
 * @param editorState
 * @param updateEditorState
 */
export const tableHandlerEnter = ({ editorState, updateEditorState }) => {
  const selection = editorState.getSelection();

  const currentContent = editorState.getCurrentContent();
  const blocks = currentContent.getBlockMap().toArray();

  const focusBlock = currentContent.getBlockForKey(selection.getFocusKey());
  let pushIndex = null;

  // Check selection block is table end
  if (focusBlock.getType() === BLOCK_TYPE.TABLE_ROW_ITEM && focusBlock.getData().get('end')) {
    pushIndex = getLastIndexBlockForTable({ selection, blocks });
  } else {
    const firstAfterBlock = currentContent.getBlockAfter(selection.getFocusKey());
    const secondAfterBlock = currentContent.getBlockAfter(firstAfterBlock?.getKey());

    if (
      // Check next block is table end
      firstAfterBlock?.getType() === BLOCK_TYPE.TABLE_ROW_ITEM &&
      firstAfterBlock.getData().get('end') &&
      // Check next->next block is not table or empty
      (!secondAfterBlock || secondAfterBlock.getType() !== BLOCK_TYPE.TABLE_ROW_ITEM)
    ) {
      pushIndex = getLastIndexBlockForTable({ selection, blocks });
    }
  }

  if (!pushIndex) {
    return;
  }

  const blockKey = genKey();
  const newRowBlock = new ContentBlock({ key: blockKey, type: 'unstyled', text: '' });
  blocks.splice(pushIndex + 1, 0, newRowBlock);

  editorState = EditorState.push(editorState, ContentState.createFromBlockArray(blocks), 'insert-fragment');
  updateEditorState(EditorState.forceSelection(editorState, SelectionState.createEmpty(blockKey)));
};

/**
 * Function for get handler if selected end cells table and pressed any keys
 * @param editorState
 * @param updateEditorState
 * @returns {boolean}
 */
export const tableHandlerDefault = ({ editorState, updateEditorState }) => {
  const selection = editorState.getSelection();
  const currentContent = editorState.getCurrentContent();
  const focusBlock = currentContent.getBlockForKey(selection.getFocusKey());

  if (focusBlock.getType() === BLOCK_TYPE.TABLE_ROW_ITEM && focusBlock.getData().get('end')) {
    tableHandlerEnter({ editorState, updateEditorState });
  }
};

/**
 * Get the index of the last block associated with the table
 * @param blocks
 * @param selection
 * @returns {null}
 */
const getLastIndexBlockForTable = ({ blocks, selection }) => {
  let pushIndex = null,
    find = false;
  blocks.forEach((block, index) => {
    find = find ? find && block.getType() === BLOCK_TYPE.TABLE_ROW_ITEM : block.getKey() === selection.getFocusKey();
    pushIndex = find ? index : pushIndex;
  });

  return pushIndex;
};

/**
 * Function for get handler if selected cell table and pressed backspace
 * @param editorState
 * @param code
 * @returns {null|*}
 */
export const tableHandlerBackspace = ({ editorState, code }) => {
  const currentBlockType = RichUtils.getCurrentBlockType(editorState);
  const selection = editorState.getSelection();

  if (currentBlockType === BLOCK_TYPE.TABLE_ROW_ITEM) {
    if (selection.getAnchorKey() !== selection.getFocusKey()) {
      return false;
    }

    // Check code for blocked remove td item by (Fn+backspace=delete and or combination)
    return selection.getIsBackward() || (code !== 'Delete' ? selection.getFocusOffset() : false);
  }

  const currentContent = editorState.getCurrentContent();
  let leftBlock, rightBlock;

  // Check highlighting
  if (selection.getAnchorKey() === selection.getFocusKey()) {
    leftBlock = currentContent.getBlockBefore(selection.getFocusKey());
    rightBlock = currentContent.getBlockAfter(selection.getFocusKey());

    // Check exist prev/next block or prev/next block is not table
    leftBlock = !leftBlock || leftBlock.getType() !== BLOCK_TYPE.TABLE_ROW_ITEM;
    rightBlock = !rightBlock || rightBlock.getType() !== BLOCK_TYPE.TABLE_ROW_ITEM;

    // For blocked remove line block between tables
    return leftBlock || rightBlock;
  } else {
    leftBlock = selection.getIsBackward() ? selection.getFocusKey() : selection.getAnchorKey();
    rightBlock = selection.getIsBackward() ? selection.getAnchorKey() : selection.getFocusKey();

    leftBlock = currentContent.getBlockForKey(leftBlock);
    rightBlock = currentContent.getBlockForKey(rightBlock);

    // Check first/last highlighting blocks if exist or not is block tables
    leftBlock = leftBlock ? leftBlock.getType() !== BLOCK_TYPE.TABLE_ROW_ITEM : false;
    rightBlock = rightBlock ? rightBlock.getType() !== BLOCK_TYPE.TABLE_ROW_ITEM : false;

    // For blocked remove table blocks if not select full table
    return leftBlock && rightBlock;
  }
};

/**
 * Function check paste text to table cell
 * @param editorState
 * @param html
 * @param updateEditorState
 * @returns {boolean}
 */
export const pasteTextTable = ({ editorState, html, updateEditorState }) => {
  const selection = editorState.getSelection();
  const currentContent = editorState.getCurrentContent();
  const focusBlock = currentContent.getBlockForKey(selection.getFocusKey());

  // Skip next process if not select table
  if (focusBlock.getType() !== BLOCK_TYPE.TABLE_ROW_ITEM) {
    return false;
  }

  // Skip next process if select end table block (hidden block)
  if (focusBlock.getData().get('end')) {
    return true;
  }

  // Get text
  let text = document.createElement('div');
  text.innerHTML = html;
  text = text.textContent || text.innerText || '';

  // Paste text to select block
  let newCurrentContent =
    selection.getStartOffset() === selection.getEndOffset()
      ? Modifier.insertText(currentContent, selection, text)
      : Modifier.replaceText(currentContent, selection, text);

  editorState = EditorState.push(editorState, newCurrentContent, 'insert-fragment');
  updateEditorState(editorState);

  return true;
};

/**
 * Function for remove table
 * @param editorState
 * @param updateEditorState
 * @param currentBlockKey
 * @param details
 */
export const removeTable = ({ editorState, updateEditorState, currentBlockKey, details }) => {
  try {
    const tableDetails = details ? details : getDetailsByCurrentCell({ editorState, currentBlockKey });
    const columnsWithHidden = tableDetails.columns + 1; // Add hidden column with end row items
    const currentContent = editorState.getCurrentContent();
    const blocks = currentContent.getBlockMap().toArray();

    blocks.splice(tableDetails.tableIndex, tableDetails.rows * columnsWithHidden);
    editorState = EditorState.push(editorState, ContentState.createFromBlockArray(blocks), 'insert-fragment');
    updateEditorState(EditorState.forceSelection(editorState, editorState.getSelection()));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log(err);
  }
};

/**
 * Function for create row or col in select table and update editor state
 * @param editorState
 * @param updateEditorState
 * @param currentBlockKey
 * @param placement
 * @param type
 */
export const addCellsToTable = ({ editorState, updateEditorState, currentBlockKey, placement, type }) => {
  try {
    const details = getDetailsByCurrentCell({ editorState, currentBlockKey });
    const columnsWithHidden = details.columns + 1; // Add hidden column with end row items
    let table = null;

    if (type === 'row') {
      table = getTableWithNewRow({ placement, details, columnsWithHidden });
    }

    if (type === 'col') {
      table = getTableWithNewCol({ placement, details, columnsWithHidden });
    }

    if (table) {
      const currentContent = editorState.getCurrentContent();
      const blocks = currentContent.getBlockMap().toArray();
      const selection = SelectionState.createEmpty(currentBlockKey);

      blocks.splice(details.tableIndex, details.rows * columnsWithHidden, ...table);
      editorState = EditorState.push(editorState, ContentState.createFromBlockArray(blocks), 'insert-fragment');
      updateEditorState(EditorState.forceSelection(editorState, selection));
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log(err);
  }
};

/**
 * Function for remove row or col in select table and update editor state
 * @param editorState
 * @param updateEditorState
 * @param currentBlockKey
 * @param type
 */
export const removeCellsFromTable = ({ editorState, updateEditorState, currentBlockKey, type }) => {
  try {
    const details = getDetailsByCurrentCell({ editorState, currentBlockKey });
    const columnsWithHidden = details.columns + 1; // Add hidden column with end row items
    let table = null;

    if (type === 'row') {
      if (details.rows === 1) {
        removeTable({ editorState, updateEditorState, currentBlockKey, details });
        return;
      }
      table = getTableWithoutRow({ details, columnsWithHidden });
    }

    if (type === 'col') {
      if (columnsWithHidden <= 2) {
        removeTable({ editorState, updateEditorState, currentBlockKey, details });
        return;
      }
      table = getTableWithoutCol({ details, columnsWithHidden });
    }

    if (table) {
      const currentContent = editorState.getCurrentContent();
      const blocks = currentContent.getBlockMap().toArray();
      const selection = table[0] ? SelectionState.createEmpty(table[0].getKey()) : editorState.getSelection();

      blocks.splice(details.tableIndex, details.rows * columnsWithHidden, ...table);
      editorState = EditorState.push(editorState, ContentState.createFromBlockArray(blocks), 'insert-fragment');
      updateEditorState(EditorState.forceSelection(editorState, selection));
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log(err);
  }
};

/**
 * Function get table blocks with new row
 * @param details
 * @param placement
 * @param columnsWithHidden
 * @returns {*[]}
 */
export const getTableWithNewRow = ({ details, placement, columnsWithHidden }) => {
  let table = [];
  details.table.forEach((block, index) => {
    const rowIndex = (index - (index % columnsWithHidden)) / columnsWithHidden;
    const colIndex = index % columnsWithHidden;
    if (rowIndex === details.rowIndex && placement === 'top' && colIndex === 0) {
      table.push(...getRow(details.columns));
    }

    table.push(block);

    if (rowIndex === details.rowIndex && placement === 'bottom' && colIndex === columnsWithHidden - 1) {
      table.push(...getRow(details.columns));
    }
  });

  return table;
};

/**
 * Function get array of new Content blocks by length
 * @param length
 * @returns {ContentBlock[]}
 */
const getRow = (length) => {
  const row = Array(length)
    .fill(0)
    .map(
      () =>
        new ContentBlock({
          key: genKey(),
          type: BLOCK_TYPE.TABLE_ROW_ITEM,
          text: '',
        }),
    );

  row.push(
    new ContentBlock({
      key: genKey(),
      type: BLOCK_TYPE.TABLE_ROW_ITEM,
      text: '',
      data: Immutable.Map({ end: true }),
    }),
  );

  return row;
};

/**
 * Function get array of blocks without current row
 * @param details
 * @param columnsWithHidden
 * @returns {*}
 */
export const getTableWithoutRow = ({ details, columnsWithHidden }) => {
  return details.table.filter((block, index) => {
    const rowIndex = (index - (index % columnsWithHidden)) / columnsWithHidden;
    return rowIndex !== details.rowIndex;
  });
};

/**
 * Function get table blocks with new col
 * @param details
 * @param placement
 * @param columnsWithHidden
 * @returns {*[]}
 */
export const getTableWithNewCol = ({ details, placement, columnsWithHidden }) => {
  let table = [];
  details.table.forEach((block, index) => {
    const colIndex = index % columnsWithHidden;
    if (placement === 'left' && colIndex === details.colIndex) {
      table.push(
        new ContentBlock({
          key: genKey(),
          type: BLOCK_TYPE.TABLE_ROW_ITEM,
          text: '',
        }),
      );
    }

    table.push(block);

    if (placement === 'right' && colIndex === details.colIndex) {
      table.push(
        new ContentBlock({
          key: genKey(),
          type: BLOCK_TYPE.TABLE_ROW_ITEM,
          text: '',
        }),
      );
    }
  });

  return table;
};

/**
 * Function get array of blocks without current col
 * @param details
 * @param columnsWithHidden
 * @returns {*}
 */
export const getTableWithoutCol = ({ details, columnsWithHidden }) => {
  return details.table.filter((block, index) => {
    const colIndex = index % columnsWithHidden;
    return colIndex !== details.colIndex;
  });
};

/**
 * Function for get detail info by select cell and table
 * @param editorState
 * @param currentBlockKey
 * @returns {{columns: *, rowIndex: null, colIndex: null, rows: *, table: *[], tableIndex: number}}
 * Detail:
 * - tableIndex: Index first cell table in main blocks
 * - columns: Count columns
 * - rows: Count rows
 * - rowIndex: Index focus row
 * - colIndex: Index focus column
 * - table: Array of blocks related current table
 */
const getDetailsByCurrentCell = ({ editorState, currentBlockKey }) => {
  const currentContent = editorState.getCurrentContent();
  let blocks = currentContent.getBlockMap().toArray();

  let table = [];
  let tableIndex = -1;
  let columns = null;
  let rows = null;
  let rowIndex = null;
  let colIndex = null;

  let prevBlock = null;
  let isFindBlock = false;
  let isFindTable = false;

  blocks.forEach((block, index) => {
    // Reset all parameters if no focus block is found
    if (prevBlock?.getType() !== BLOCK_TYPE.TABLE_ROW_ITEM && block.getType() === BLOCK_TYPE.TABLE_ROW_ITEM) {
      if (isFindBlock) {
        isFindTable = true;
        return;
      }
      table = [];
      tableIndex = index;
      columns = -1;
      rows = 0;
      rowIndex = 0;
      colIndex = -1;
    }

    // Skip the following code if the selected table is found
    if (isFindTable) {
      return;
    }

    // Save table blocks for next processes
    if (block.getType() === BLOCK_TYPE.TABLE_ROW_ITEM) {
      table.push(block);
    }

    // Increase the row index if a new row is found
    if (prevBlock?.getData().get('end') && block.getType() === BLOCK_TYPE.TABLE_ROW_ITEM) {
      rows++;
      // Increase row index reset column index if focus block is not found
      if (!isFindBlock) {
        colIndex = -1;
        rowIndex++;
      }
    }

    if (block.getType() === BLOCK_TYPE.TABLE_ROW_ITEM && !block.getData().get('end')) {
      columns += rows === 0 ? 1 : 0; // Up in first row
      colIndex += isFindBlock ? 0 : 1; // Up if not find focus block
    }

    // Check current block is focus block
    isFindBlock = isFindBlock ? isFindBlock : block.getKey() === currentBlockKey;
    prevBlock = block;
  });

  return {
    tableIndex,
    columns: columns + 1,
    rows: rows + 1,
    rowIndex,
    colIndex,
    table,
  };
};
